import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Divider,
  TextField,
  Button,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import SellIcon from '@mui/icons-material/Sell';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BugReportIcon from '@mui/icons-material/BugReport';
import LockIcon from '@mui/icons-material/Lock';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HistoryIcon from '@mui/icons-material/History';
import TodayIcon from '@mui/icons-material/Today';
import ArchiveIcon from '@mui/icons-material/Archive';
import {
  fetchTodayBrief,
  fetchArchivedDates,
  fetchArchivedBrief,
  triggerArchive,
} from '../api/todayBrief';
import type { TodayBrief, ArchivedDateItem, ArchivedBriefResult } from '../api/todayBrief';
import { formatMoney, formatPct, formatNumber } from '../utils/format';

const REFRESH_INTERVAL_LIVE_MS = 30000; // 实时：30s 一次
const REFRESH_INTERVAL_HISTORY_MS = 0;    // 历史：不刷新

type ViewMode = 'live' | 'history';

/**
 * 今日简报 — 首页新增板块
 *
 * 双视图：
 * - 「今日实时」(live)：15s 自动刷新，支持交易进行时观察卖出/买入触发
 * - 「历史归档」(history)：用户选日期，从 daily_briefs 表读取只读快照
 *
 * 卡片结构（live）：
 *   1. 卖出（该出手的）— 当前持仓 + 实时价 vs 止盈止损线
 *   2. 买入（已尝试的） — 上一筛选日 → 今日 10:00 尝试买入的目标，结果对照
 *   3. 待购（明早 10:00）— 今日收盘筛选 → 次日 10:00 待购（新增）
 *   4. 今日实际成交明细
 *   5. 综合状态（盈亏 / 自检 / 挂起）
 *
 * 历史模式下，所有卡片都从归档 JSON 渲染，不请求实时数据。
 */
export default function TodayBriefPage() {
  const [mode, setMode] = useState<ViewMode>('live');
  const [dates, setDates] = useState<ArchivedDateItem[]>([]);
  const [historyDate, setHistoryDate] = useState<string>('');

  const [brief, setBrief] = useState<TodayBrief | null>(null);
  const [archived, setArchived] = useState<ArchivedBriefResult | null>(null);

  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'info' | 'success' | 'warning' | 'error' } | null>(null);
  const isUnmounted = useRef(false);

  // ===== 数据加载 =====
  const loadLive = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTodayBrief();
      if (isUnmounted.current) return;
      setBrief(data);
    } catch (e: any) {
      if (isUnmounted.current) return;
      setError(e.message || '加载今日简报失败');
    } finally {
      if (!isUnmounted.current) setLoading(false);
    }
  }, []);

  const loadDates = useCallback(async () => {
    try {
      const ds = await fetchArchivedDates();
      if (!isUnmounted.current) return;
      setDates(ds);
    } catch (e: any) {
      console.error('load dates error', e);
    }
  }, []);

  const loadHistory = useCallback(async (date: string) => {
    if (!date) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchArchivedBrief(date);
      if (isUnmounted.current) return;
      setArchived(r);
      if (r.rejected) {
        setSnackbar({ open: true, message: `${date} 归档时自检未通过，仅保存了拒绝记录`, severity: 'warning' });
      }
    } catch (e: any) {
      if (isUnmounted.current) return;
      setError(e.message || '加载历史归档失败');
    } finally {
      if (!isUnmounted.current) setLoading(false);
    }
  }, []);

  // ===== 切换到历史模式 =====
  const switchToHistory = useCallback(async (date: string) => {
    setMode('history');
    setHistoryDate(date);
    if (dates.length === 0) await loadDates();
    await loadHistory(date);
  }, [dates.length, loadDates, loadHistory]);

  const switchToLive = useCallback(async () => {
    setMode('live');
    setArchived(null);
    await loadLive();
  }, [loadLive]);

  // ===== 首次 mount: 拉日期列表 + 拉实时 =====
  useEffect(() => {
    isUnmounted.current = false;
    loadLive();
    loadDates();
    return () => { isUnmounted.current = true; };
  }, [loadLive, loadDates]);

  // ===== 自动刷新（仅 live） =====
  useEffect(() => {
    if (mode !== 'live') return;
    const t = window.setInterval(loadLive, REFRESH_INTERVAL_LIVE_MS);
    return () => window.clearInterval(t);
  }, [mode, loadLive]);

  // ===== 手动归档 =====
  const onArchive = useCallback(async () => {
    if (archiving) return;
    setArchiving(true);
    try {
      const r = await triggerArchive();
      setSnackbar({
        open: true,
        message: r.archived ? `已归档 ${r.date}: ${r.reason}` : `归档被拒：${r.reason}`,
        severity: r.archived ? 'success' : 'warning',
      });
      await loadDates();
    } catch (e: any) {
      setSnackbar({ open: true, message: '归档失败: ' + (e.message || e), severity: 'error' });
    } finally {
      setArchiving(false);
    }
  }, [archiving, loadDates]);

  // ===== 渲染分支 =====
  if (loading && !brief && !archived) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !brief && !archived) {
    return (
      <Alert severity="error" sx={{ my: 2 }} action={
        <IconButton onClick={mode === 'live' ? loadLive : () => loadHistory(historyDate)}><RefreshIcon /></IconButton>
      }>{error}</Alert>
    );
  }

  const viewBrief = (mode === 'live' ? brief : archived?.brief) as TodayBrief | null;
  if (!viewBrief) return null;

  const tradeDate = viewBrief.tradeDate;
  const { market, sell, buy, pending, todayTrades, globalLock, selfCheck, dailyPnl, pendingBuys } = viewBrief;
  const archivedFor = mode === 'history' ? archived : null;

  // 止损线口径（2026-09-17）：止损可能来自「近 N 日最低价」(loss_30d_low_stop_days)、
  // 「亏损 T%」(loss_threshold) 或两者取高，不能一律写成「-5%」。
  // 兼容历史归档（brief_json 里没有新字段）：缺失时回退到 lossThreshold 判断。
  const stopEnabled = sell.stopLossEnabled ?? (sell.lossThreshold > 0);
  const stopDaysVal = sell.stopDays ?? null;
  const stopLineLabel = !stopEnabled
    ? '未启用'
    : stopDaysVal && sell.lossThreshold > 0
      ? `近${stopDaysVal}日低／-${sell.lossThreshold}%`
      : stopDaysVal
        ? `近${stopDaysVal}日最低`
        : `-${sell.lossThreshold}%`;
  const stopLineDesc = !stopEnabled
    ? '（未启用止损）'
    : stopDaysVal && sell.lossThreshold > 0
      ? `/${stopDaysVal}日最低价与-${sell.lossThreshold}%取高`
      : stopDaysVal
        ? `/${stopDaysVal}日最低价`
        : `/${sell.lossThreshold}%`;
  const stopSummary = stopEnabled
    ? `（触发后由盘中卖出检查执行）`
    : '（未启用止损条件，不会卖出）';

  // ===== 顶部状态行（含视图切换 + 日期选择器） =====
  const HeaderStatus = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        交易日：<strong style={{ color: '#1976d2' }}>{tradeDate}</strong>
      </Typography>
      <Chip
        size="small"
        icon={market.shChangePct >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
        label={`沪 ${market.shChangePct >= 0 ? '+' : ''}${(market.shChangePct ?? 0).toFixed(2)}%`}
        color={market.shChangePct >= 0 ? 'error' : 'success'}
        variant="outlined"
      />
      <Chip
        size="small"
        icon={market.szChangePct >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
        label={`深 ${market.szChangePct >= 0 ? '+' : ''}${(market.szChangePct ?? 0).toFixed(2)}%`}
        color={market.szChangePct >= 0 ? 'error' : 'success'}
        variant="outlined"
      />
      <Chip size="small" label={`涨跌 ${market.upCount}/${market.downCount}`} variant="outlined" />
      <Chip
        size="small"
        color="info"
        icon={<ArchiveIcon />}
        label="整个简报每天 17:00 自动归档"
        variant="outlined"
        sx={{ borderColor: 'info.main' }}
      />
      <Box sx={{ flex: 1 }} />

      {/* 视图切换 */}
      <Stack direction="row" spacing={1} alignItems="center">
        {mode === 'live' ? (
          <Chip
            size="small"
            color="info"
            icon={<TodayIcon />}
            label="实时（30s 刷新）"
          />
        ) : (
          <Tooltip title="返回实时今日简报">
            <Chip
              size="small"
              color="info"
              icon={<TodayIcon />}
              label="返回实时"
              onClick={switchToLive}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        )}
        {mode === 'history' && (
          <Chip
            size="small"
            color="secondary"
            icon={<HistoryIcon />}
            label={`历史归档 ${historyDate}`}
          />
        )}
        {mode === 'live' && (
          <TextField
            select={false}
            size="small"
            type="date"
            value={historyDate || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v && dates.some((d) => d.date === v)) {
                switchToHistory(v);
              } else if (v) {
                // 新日期首次查询
                switchToHistory(v);
              }
            }}
            inputProps={{ max: new Date().toISOString().slice(0, 10) }}
            sx={{ width: 160 }}
            helperText="查看历史归档"
          />
        )}
        {mode === 'live' && (
          <Tooltip title={archiving ? '归档中…' : '立即归档当前简报（17:00 自动任务之外的手动触发）'}>
            <span>
              <IconButton onClick={onArchive} disabled={archiving} size="small" color="primary">
                <ArchiveIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {mode === 'live' && (
          <IconButton onClick={loadLive} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        )}
      </Stack>
    </Box>
  );

  // 归档状态徽章（仅 history 模式显示）
  const ArchiveBadge = archivedFor && (
    <Alert
      severity={archivedFor.rejected ? 'error' : (archivedFor.selfCheckStatus === 'ok' ? 'success' : 'warning')}
      icon={archivedFor.rejected ? <CancelIcon /> : <CheckCircleIcon />}
      sx={{ mb: 2, '& .MuiAlert-message': { fontSize: 13 } }}
    >
      <strong>归档时间：</strong>{archivedFor.archivedAt}
      &nbsp;·&nbsp;
      <strong>自检状态：</strong>{archivedFor.selfCheckStatus === 'ok' ? '全部通过' :
        archivedFor.selfCheckStatus === 'fixed' ? `自动修复 ${archivedFor.issuesFound} 项` :
          archivedFor.selfCheckStatus}
      {archivedFor.warnings.length > 0 && (
        <>
          &nbsp;·&nbsp;
          <strong>遗留：</strong>{archivedFor.warnings.length} 项
        </>
      )}
      {archivedFor.rejected && (
        <>
          <br />
          <strong>⚠️ 归档被拒：</strong>{archivedFor.selfCheckStatus === 'error' ? '存在未修复的 error 级问题' : '构建失败'}
        </>
      )}
    </Alert>
  );

  // 历史归档日期选择器中可用列表（chip 化快捷选）
  const HistoryDateChips = mode === 'history' && dates.length > 0 && (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
      {dates.slice(0, 10).map((d) => (
        <Chip
          key={d.date}
          size="small"
          variant={d.date === historyDate ? 'filled' : 'outlined'}
          color={d.rejected ? 'error' : (d.selfCheckStatus === 'ok' ? 'success' : 'default')}
          label={`${d.date}${d.rejected ? '✗' : ''}`}
          onClick={() => switchToHistory(d.date)}
          sx={{ cursor: 'pointer' }}
        />
      ))}
    </Box>
  );

  // ===== 卖出区块 =====
  const SellSection = (
    <Card sx={{ mb: 2, boxShadow: 1 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <SectionHeader
          icon={<SellIcon fontSize="small" />}
          color={sell.triggerCount > 0 ? 'warning' : 'success'}
          title={
            <>
              <strong>卖出（该出手的）：{sell.triggerCount}</strong> 只
              {sell.triggerCount > 0 ? <WarningIcon sx={{ fontSize: 18, ml: 0.5, verticalAlign: 'middle' }} color="warning" /> : <CheckCircleIcon sx={{ fontSize: 18, ml: 0.5, verticalAlign: 'middle' }} color="success" />}
            </>
          }
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
          {sell.list.length === 0
            ? '当前无持仓。'
            : sell.triggerCount > 0
              ? `共 ${sell.list.length} 只持仓，${sell.triggerCount} 只触及止盈/止损线${stopSummary}；其余 ${sell.holdCount} 只继续持有。`
              : `共 ${sell.list.length} 只持仓，今日均未触及止盈${sell.profitThreshold}%${stopLineDesc}，已经过系统盘中卖出检查（60 秒/次）。`}
        </Typography>

        {sell.list.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <Th>持仓</Th>
                  <Th align="right">买入价</Th>
                  <Th align="right">止盈线<br /><small style={{ fontWeight: 400, color: '#888' }}>+{sell.profitThreshold}%</small></Th>
                  <Th align="right">止损线<br /><small style={{ fontWeight: 400, color: '#888' }}>{stopLineLabel}</small></Th>
                  <Th align="right">今日实际</Th>
                  <Th>结论</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {sell.list.map((s) => (
                  <TableRow key={s.code} hover>
                    <Td><NameCell code={s.code} name={s.name} /></Td>
                    <TdNum>{formatNumber(s.buyPrice)}</TdNum>
                    <TdNum sx={{ color: 'error.main' }}>{formatNumber(s.takeProfitPrice)}</TdNum>
                    <TdNum sx={{ color: 'success.main' }}>
                      {s.stopLossPrice !== null && s.stopLossPrice !== undefined ? formatNumber(s.stopLossPrice) : '未启用'}
                    </TdNum>
                    <TdNum>
                      <span style={{ color: '#d32f2f' }}>高 {formatNumber(s.intradayHigh)}</span>{' / '}
                      <span style={{ color: '#2e7d32' }}>低 {formatNumber(s.intradayLow)}</span>
                    </TdNum>
                    <Td>
                      {s.status === 'triggered_profit' && (
                        <Chip size="small" color="error" icon={<WarningIcon />} label="止盈已触发" />
                      )}
                      {s.status === 'triggered_loss' && (
                        <Chip size="small" color="warning" icon={<WarningIcon />} label="止损已触发" />
                      )}
                      {s.status === 'hold' && (
                        <Chip size="small" color="success" variant="outlined" label="持有" />
                      )}
                    </Td>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  // ===== 买入（已尝试的）— 上一筛选日 → 今日 10:00 =====
  const BuySection = (
    <Card sx={{ mb: 2, boxShadow: 1 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <SectionHeader
          icon={<ShoppingCartIcon fontSize="small" />}
          color={buy.tradedCount === buy.candidateCount && buy.candidateCount > 0 ? 'success' : (globalLock.locked ? 'error' : 'warning')}
          title={
            <>
              <strong>买入（今日 10:00 已尝试）：{buy.candidateCount}</strong> 只
              {buy.tradedCount === buy.candidateCount && buy.candidateCount > 0 ? (
                <CheckCircleIcon sx={{ fontSize: 18, ml: 0.5, verticalAlign: 'middle' }} color="success" />
              ) : (
                <CancelIcon sx={{ fontSize: 18, ml: 0.5, verticalAlign: 'middle' }} color={globalLock.locked ? 'error' : 'warning'} />
              )}
            </>
          }
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
          来源：<strong>{buy.candidateDate}</strong> 收盘筛选（{buy.source}） → 本应在 <strong>{tradeDate} 10:00</strong> 尝试以开盘价买入。<br />
          {buy.candidateCount === 0
            ? '今日无买入目标（上一筛选日无候选股）。'
            : buy.tradedCount === buy.candidateCount
              ? `${buy.candidateCount} 只候选股均按 10:00 价格成交。`
              : buy.tradedCount > 0
                ? `${buy.candidateCount} 只候选股中，${buy.tradedCount} 只已买入，${buy.blockedCount} 只未成交。`
                : `${buy.candidateCount} 只候选股均未成交。${globalLock.locked ? '原因：' + globalLock.reasons.join('；') : ''}`}
        </Typography>

        {buy.list.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <Th>股票</Th>
                  <Th align="right">10:00 价</Th>
                  <Th align="right">拟买数量</Th>
                  <Th align="right">金额</Th>
                  <Th>状态</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {buy.list.map((b) => (
                  <TableRow key={b.code} hover>
                    <Td><NameCell code={b.code} name={b.name} /></Td>
                    <TdNum>{formatNumber(b.priceAt10am)}</TdNum>
                    <TdNum>
                      {b.traded
                        ? `${formatNumber(b.plannedQuantity || Math.round(b.actualAmount / (b.priceAt10am || 1)))} 股`
                        : (b.plannedQuantity > 0 ? `${formatNumber(b.plannedQuantity)} 股` : '金额不足')}
                    </TdNum>
                    <TdNum>{b.traded ? formatMoney(b.actualAmount) : formatMoney(b.plannedAmount)}</TdNum>
                    <Td>
                      {b.traded ? (
                        <Chip size="small" color="success" icon={<CheckCircleIcon />} label="已买入" />
                      ) : (
                        <Chip size="small" color="error" variant="outlined" icon={<CancelIcon />} label="未成交" />
                      )}
                    </Td>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {buy.blockedCount > 0 && globalLock.locked && (
          <Alert severity="error" icon={<LockIcon />} sx={{ mt: 1.5, '& .MuiAlert-message': { fontSize: 13 } }}>
            <strong>系统没有成交</strong>，原因是全局门控触发了「暂停买入」：{globalLock.reasons.join('；')}
            {globalLock.triggeredAt && <span style={{ color: '#666' }}>（{triggeredAtText(globalLock.triggeredAt)}）</span>}
            。按你配置的整体规则，系统判定 <code>pauseBuy</code>，阻止了买入。
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  // ===== 待购（明早 10:00）— 今日筛选 → 次日 10:00 =====
  const PendingSection = (
    <Card sx={{ mb: 2, boxShadow: 1, border: '1px solid', borderColor: 'primary.light' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <SectionHeader
          icon={<ShoppingCartIcon fontSize="small" />}
          color={pending.candidateCount > 0 ? 'primary' : 'default'}
          title={
            <>
              <strong>待购（明早 10:00）：{pending.candidateCount}</strong> 只
              {pending.candidateCount > 0 && <CheckCircleIcon sx={{ fontSize: 18, ml: 0.5, verticalAlign: 'middle' }} color="primary" />}
            </>
          }
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
          来源：<strong>{pending.candidateDate}</strong> 收盘筛选（{pending.source}） → 将于 <strong>次日 10:00</strong> 开盘价尝试买入。
          {pending.candidateCount === 0 && '（今日 15:00 尚未筛选或筛出 0 只）'}
        </Typography>

        {pending.list.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <Th>股票</Th>
                  <Th align="right">筛选价</Th>
                  <Th align="right">拟买数量</Th>
                  <Th align="right">金额</Th>
                  <Th>匹配规则</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {pending.list.map((p) => {
                  let rules = '';
                  try { rules = JSON.parse(p.matchedRules || '[]').join('、'); } catch { rules = p.matchedRules; }
                  return (
                    <TableRow key={p.code} hover>
                      <Td><NameCell code={p.code} name={p.name} /></Td>
                      <TdNum>{formatNumber(p.screenedPrice)}</TdNum>
                      <TdNum>{p.plannedQuantity > 0 ? `${formatNumber(p.plannedQuantity)} 股` : '金额不足'}</TdNum>
                      <TdNum>{formatMoney(p.plannedAmount)}</TdNum>
                      <Td>
                        <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {rules || '--'}
                        </Typography>
                      </Td>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );

  // ===== 今日成交明细 =====
  const TradesSection = todayTrades.length > 0 && (
    <Card sx={{ mb: 2, boxShadow: 1 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <SectionHeader
          icon={<CheckCircleIcon fontSize="small" />}
          color="success"
          title={<><strong>今日实际成交：{todayTrades.length}</strong> 笔</>}
        />
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <Th>时间</Th>
                <Th>股票</Th>
                <Th>方向</Th>
                <Th align="right">价格</Th>
                <Th align="right">数量</Th>
                <Th align="right">金额</Th>
                <Th align="right">盈亏</Th>
                <Th>原因</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {todayTrades.map((t) => (
                <TableRow key={t.id} hover>
                  <Td sx={{ fontFamily: 'monospace', fontSize: 12 }}>{(t.tradeTime || '').slice(11, 19)}</Td>
                  <Td><NameCell code={t.code} name={t.name} /></Td>
                  <Td>
                    <Chip size="small" color={t.direction === 'buy' ? 'primary' : 'warning'} label={t.direction === 'buy' ? '买入' : '卖出'} />
                  </Td>
                  <TdNum>{formatNumber(t.price)}</TdNum>
                  <TdNum>{formatNumber(t.quantity)}</TdNum>
                  <TdNum>{formatMoney(t.amount)}</TdNum>
                  <TdNum sx={{ color: (t.profit ?? 0) >= 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
                    {t.direction === 'sell' ? ((t.profit >= 0 ? '+' : '') + formatMoney(t.profit)) : '--'}
                  </TdNum>
                  <Td><Typography variant="caption" color="text.secondary">{t.reason || '--'}</Typography></Td>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  // ===== 底部综合状态卡 =====
  const FooterSection = (
    <Card sx={{ boxShadow: 1 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <SectionHeader
          icon={<BugReportIcon fontSize="small" />}
          color="info"
          title={<>系统状态一览</>}
        />

        <Stack spacing={1.5}>
          {dailyPnl ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 80 }}>今日盈亏：</Typography>
              <Chip
                size="small"
                color={(dailyPnl.realizedProfit ?? 0) >= 0 ? 'error' : 'success'}
                label={`已实现 ${formatMoney(dailyPnl.realizedProfit)}`}
              />
              <Chip
                size="small"
                color={(dailyPnl.unrealizedProfit ?? 0) >= 0 ? 'error' : 'success'}
                label={`浮盈 ${formatMoney(dailyPnl.unrealizedProfit)}`}
                variant="outlined"
              />
              <Chip size="small" label={`总资产 ${formatMoney(dailyPnl.totalAssets)}`} variant="outlined" />
              <Chip size="small" label={`持仓 ${dailyPnl.heldCount} 只`} variant="outlined" />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">暂无今日盈亏记录（15:05 收盘后自动记录）。</Typography>
          )}

          {selfCheck && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 80 }}>系统自检：</Typography>
              <Chip
                size="small"
                color={selfCheck.status === 'ok' ? 'success' : selfCheck.status === 'fixed' ? 'warning' : 'error'}
                label={
                  selfCheck.status === 'ok' ? '正常'
                    : selfCheck.status === 'fixed' ? `已自动修复 ${selfCheck.issuesFixed} 项`
                    : `异常 ${(selfCheck.issuesFound ?? 0) - (selfCheck.issuesFixed ?? 0)} 项`
                }
              />
              <Typography variant="caption" color="text.secondary">
                上次：{(selfCheck.checkTime || '').slice(11, 19)} · 耗时 {selfCheck.durationMs}ms
              </Typography>
            </Box>
          )}

          {pendingBuys.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 80 }}>挂起补买：</Typography>
              {pendingBuys.map((p, i) => (
                <Chip
                  key={i}
                  size="small"
                  color={p.status === 'pending' ? 'warning' : 'default'}
                  variant="outlined"
                  label={`${p.name || p.code}（${p.status === 'pending' ? '挂起' : '已放弃'}）${p.error ? '：' + p.error : ''}`}
                />
              ))}
            </Box>
          )}

          {mode === 'live' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              💡 整个今日简报（含卖出/买入/待购/成交/状态）将于每日 17:00 自动跑自检，自检无 error 后归档供历史回看。
              你也可以点击右上角 📥 图标立即触发归档。
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          {mode === 'live' ? '今日简报' : `历史归档：${historyDate}`}
        </Typography>
      </Box>
      {HeaderStatus}
      {HistoryDateChips}
      {ArchiveBadge}
      {SellSection}
      {BuySection}
      {PendingSection}
      {TradesSection}
      {FooterSection}

      {/* 简易 snackbar */}
      {snackbar?.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(null)}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1500, minWidth: 240 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </Box>
  );
}

// ===== 小组件 =====

function SectionHeader({ icon, color, title }: { icon: React.ReactNode; color: string; title: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{ color: `${color}.main`, display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
    </Box>
  );
}

function Th({ children, ...rest }: any) {
  return (
    <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.75, px: 1, whiteSpace: 'nowrap', bgcolor: 'grey.50' }} {...rest}>
      {children}
    </TableCell>
  );
}

function Td({ children, sx, ...rest }: any) {
  return (
    <TableCell sx={{ fontSize: 12, py: 0.75, px: 1, ...sx }} {...rest}>
      {children}
    </TableCell>
  );
}

function TdNum({ children, sx, ...rest }: any) {
  return (
    <TableCell align="right" sx={{ fontSize: 12, py: 0.75, px: 1, fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', ...sx }} {...rest}>
      {children}
    </TableCell>
  );
}

function NameCell({ code, name }: { code: string; name: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{name || '--'}</Typography>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'monospace' }}>{code}</Typography>
    </Box>
  );
}

function triggeredAtText(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('zh-CN', { hour12: false });
  } catch {
    return '';
  }
}
